import React, {
  ComponentType,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { capitalize } from 'lodash';
import {
  Window,
  WindowActionsBar,
  WindowActionsEvent
} from '@progress/kendo-react-dialogs';
import {
  Checkbox,
  Input,
  InputChangeEvent,
  RadioButton,
  RadioButtonChangeEvent
} from '@progress/kendo-react-inputs';
import { useSessionStorage } from 'usehooks-ts';
import {
  TabStrip,
  TabStripSelectEventArguments,
  TabStripTab
} from '@progress/kendo-react-layout';
import {
  ExternalDropZone,
  Upload,
  UploadOnAddEvent
} from '@progress/kendo-react-upload';
import cloneDeep from 'lodash.clonedeep';
import { Button } from '@progress/kendo-react-buttons';
import { Error, Label } from '@progress/kendo-react-labels';
import {
  ComboBoxChangeEvent,
  DropDownList,
  DropDownListChangeEvent
} from '@progress/kendo-react-dropdowns';

import {
  formatBytes,
  GetResourceById,
  GetResourceByValue,
  GetResourceByTypes,
  UpdateGameDocState,
  GetFileExtensionsByMediaType,
  isSystemDefaultResource,
  sortResourcesWithSystemLast
} from '../../../utils/game-document';
import { YesNoDialogResult } from '../../../types/dialog-result';
import { MapImage, Resource } from '../../../types/game-document';
import YesNoDialog from '../../../components/dialogs/yes-no-dialog';
import { GameDocumentContext } from '../../../contexts/game-document';
import PdfViewerComponent from '../../../components/media/pdf-viewer';
import { detectVideoPlatform } from '../../../components/editor/insert-media-dialog';
import { DEFAULT_RESOURCE_CATEGORIES } from '../../../constants/resource-categories';
import { Resource as ResourceEntity } from '../../../types/game-document/entities/resource';
import { ResourceCategoryComboBox } from '../../../components/combobox/resource-category-combobox';

export interface UploadedImage {
  blobUrl: string;
  mimeType: string;
  fileName: string;
  resource?: Resource;
  size: number;
  name?: string;
  category?: string;
}

export interface UploadImageProps {
  title?: string;
  imageUrl?: string;
  resourceId?: string;
  imageUsage?: number;
  hideUrlTab?: boolean;
  mapImages?: MapImage[];
  toggleDialog: Function;
  customFileSize?: number;
  hideUploadTab?: boolean;
  isMultipleFiles?: boolean;
  isSupportUpload?: boolean;
  hideResourcesTab?: boolean;
  defaultSelectedCategory?: string;
  fileType: ('image' | 'audio' | 'video' | 'pdf')[];
  onSubmit?: (uploadedImage: UploadedImage) => void;
  onClose?: (event: WindowActionsEvent) => void;
  onSubmitMultipleFiles?: (files: UploadedImage[]) => void;
}

const sizeLimit = {
  image: 512000, // 500KB
  audio: 5242880, // 5MB
  video: 20971520, // 20MB
  pdf: 20971520, // 20MB
  worldZoneImage: 3145728 // 3MB space for each maps
};

export const ResourceWindow: ComponentType<UploadImageProps> = ({
  imageUrl,
  fileType,
  mapImages,
  resourceId,
  imageUsage,
  toggleDialog,
  customFileSize,
  hideUrlTab = false,
  hideUploadTab = false,
  title = 'Upload image',
  defaultSelectedCategory,
  isMultipleFiles = false,
  hideResourcesTab = false,
  onClose = () => {},
  onSubmit = () => {},
  onSubmitMultipleFiles = () => {},
  isSupportUpload = false,
  ...props
}: UploadImageProps) => {
  const uploadRef = React.createRef<Upload>();
  const [state, setState] = useContext(GameDocumentContext);

  interface BulkMediaItem {
    file: File;
    name?: string;
    category?: string;
    blobUrl?: string;
  }

  const getFileNameWithoutExtension = (filename: string) => {
    if (!filename) return '';
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1) return filename;
    return filename.substring(0, lastDotIndex);
  };

  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  const [files, setFiles] = useState<BulkMediaItem[]>([]);
  const [resourceName, setResourceName] = useState<string>('');
  const [resourceCategory, setResourceCategory] = useState<string>('');
  const [bulkCategory, setBulkCategory] = useState<string>('');
  const [pendingBulkCategory, setPendingBulkCategory] = useState<string>('');
  const [showOverwriteConfirm, setShowOverwriteConfirm] =
    useState<boolean>(false);

  const [selected, setSelected] = useState<number>(0);
  const [isError, setIsError] = useState<string>('');
  const [isValidFileExtension, setIsValidFileExtension] =
    useState<boolean>(false);
  const [lastImageId, setLastImageId] = useState<string>('');
  const [lastImageUrl, setLastImageUrl] = useState<string>('');
  const [maxImageSize, setMaxImageSize] = useState<string>('');
  const [isFileEmpty, setIsFileEmpty] = useState<boolean>(true);
  const allCategories = useMemo(() => {
    const existingCategories =
      state.gameDocument?.resources
        ?.map((r) => r.category)
        .filter((c): c is string => !!c && c.trim() !== '') ?? [];
    const customCategories =
      state.gameDocument?.assets?.additionalResourceCategories ?? [];
    const merged = new Set([
      ...DEFAULT_RESOURCE_CATEGORIES,
      ...existingCategories,
      ...customCategories
    ]);
    return Array.from(merged).sort();
  }, [state.gameDocument]);

  const findMatchingCategory = (categoryName?: string) => {
    if (!categoryName) return 'All';
    const match = allCategories.find(
      (c) => c.trim().toLowerCase() === categoryName.trim().toLowerCase()
    );
    return match || categoryName;
  };

  const [selectedMediaCategory, setSelectedMediaCategory] = useState<string>(
    () =>
      defaultSelectedCategory
        ? findMatchingCategory(defaultSelectedCategory)
        : 'All'
  );
  const [searchResourceText, setSearchResourceText] = useState<string>('');
  const [showSystemDefaults, setShowSystemDefaults] = useSessionStorage<boolean>(
    'show-system-default-resources',
    false
  );

  const mediaLibraryCategoryOptions = useMemo(() => {
    return ['All', ...allCategories];
  }, [allCategories]);

  useEffect(() => {
    if (defaultSelectedCategory) {
      setSelectedMediaCategory(findMatchingCategory(defaultSelectedCategory));
    } else {
      setSelectedMediaCategory('All');
    }
  }, [defaultSelectedCategory, allCategories]);

  const allMediaResources = useMemo(() => {
    if (!state.gameDocument) return [];
    return (
      GetResourceByTypes(state.gameDocument, fileType).filter(
        (item) => !!item.name
      ) ?? []
    );
  }, [state.gameDocument, fileType]);

  const filteredMediaResources = useMemo(() => {
    let result = allMediaResources.filter((item) => {
      const matchesCategory =
        selectedMediaCategory === 'All' ||
        (item.category &&
          item.category.trim().toLowerCase() ===
            selectedMediaCategory.trim().toLowerCase());

      const query = searchResourceText.trim().toLowerCase();
      const matchesSearch =
        !query || (item.name && item.name.toLowerCase().includes(query));

      return matchesCategory && matchesSearch;
    });

    if (!showSystemDefaults) {
      result = result.filter(
        (item) => !isSystemDefaultResource(item, state.gameDocument)
      );
    } else {
      result = sortResourcesWithSystemLast(result, state.gameDocument);
    }

    return result;
  }, [allMediaResources, selectedMediaCategory, searchResourceText, showSystemDefaults, state.gameDocument]);
  const [isImageValid, setIsImageValid] = useState<boolean>(false);
  const [showDefaultPhoto, setShowDefaultPhoto] = useState<boolean>(true);
  const [isLargeImageSize, setIsLargeImageSize] = useState<boolean>(false);
  const [lastUploadedImage, setLastUploadedImage] = useState<UploadedImage>(
    {} as UploadedImage
  );

  const existingResource = useMemo(() => {
    if (resourceId && state.gameDocument) {
      return GetResourceById(state.gameDocument, resourceId);
    }
    if (imageUrl && state.gameDocument) {
      return GetResourceByValue(state.gameDocument, imageUrl);
    }
    return undefined;
  }, [resourceId, imageUrl, state.gameDocument]);

  useEffect(() => {
    if (existingResource) {
      setResourceName(existingResource.name || '');
      setResourceCategory(existingResource.category || '');
    }

    if (defaultSelectedCategory && !existingResource?.category) {
      setResourceCategory(defaultSelectedCategory);
    }
  }, [existingResource, defaultSelectedCategory]);

  const isNameInvalid = (name: string) => {
    return !name || name.trim() === '' || uuidRegex.test(name.trim());
  };

  const hasInvalidNames = isMultipleFiles
    ? files.some((file) => isNameInvalid(file.name || ''))
    : isNameInvalid(resourceName);

  const isMultipleFilesSaveDisabled = () => {
    if (isSupportUpload) {
      return files.length === 0;
    }
    return files.some((file) => {
      const isNameErr = isNameInvalid(file.name || '');
      const isCatErr = !file.category || file.category.trim() === '';
      return isNameErr || isCatErr;
    });
  };

  const isSaveDisabled =
    selected === 2
      ? isValidFileExtension || isFileEmpty || isLargeImageSize
      : isValidFileExtension ||
        isFileEmpty ||
        isLargeImageSize ||
        (isMultipleFiles
          ? isMultipleFilesSaveDisabled()
          : !isSupportUpload &&
            (!resourceCategory || isNameInvalid(resourceName)));

  const allowedExtension = useMemo(() => {
    const map: Record<string, string> = {
      image: 'image/*',
      audio: 'audio/*',
      video: 'video/*',
      pdf: 'application/pdf'
    };

    return fileType.map((t) => map[t]).join(', ');
  }, [fileType]);

  const onUploadClick = () => {
    if (uploadRef && uploadRef.current && uploadRef.current.actionElement) {
      uploadRef.current.actionElement.click();
    }
  };

  const onTabSelect = (event: TabStripSelectEventArguments) => {
    setFiles([]);
    setLastImageUrl('');
    setLastImageId('');
    setLastUploadedImage({} as UploadedImage);
    setIsLargeImageSize(false);
    setIsFileEmpty(true);
    setSelected(event.selected);
  };

  const checkFileSize = (maxSize: number, fileSize: number) => {
    if (maxSize && fileSize && fileSize > maxSize) {
      setIsLargeImageSize(true);
      setMaxImageSize(formatBytes(maxSize, 2));
      return true;
    } else {
      if (isLargeImageSize) {
        setIsLargeImageSize(false);
        return false;
      }
    }
  };

  const getLimitByMimeType = (mime: string | undefined) => {
    if (!mime) return 0;

    if (customFileSize !== undefined) return customFileSize;
    if (imageUsage !== undefined) return sizeLimit.worldZoneImage - imageUsage;

    if (mime.startsWith('image/')) return sizeLimit.image;
    if (mime.startsWith('audio/')) return sizeLimit.audio;
    if (mime.startsWith('video/')) return sizeLimit.video;
    if (mime.includes('pdf')) return sizeLimit.pdf;

    return 0; // unknown type
  };

  // including restrictions check (such as max size, extension, if applied - depends where the source is from)
  const onDropFiles = (event: UploadOnAddEvent) => {
    let uploadedFiles: BulkMediaItem[] = [];

    event.newState.forEach((file) => {
      if (file && file.getRawFile) {
        const raw = file.getRawFile();

        // remove URL and resource if exists
        setLastImageUrl('');
        setLastImageId('');
        setLastUploadedImage({} as UploadedImage);

        // check max size
        const maxSize = getLimitByMimeType(raw.type);
        checkFileSize(maxSize ?? 0, file.size ?? 0);

        // check file extension(s), set isValidFileExtension to false if file extension is matching with the related extension list
        let fileExtensionList = GetFileExtensionsByMediaType(raw.type);

        if (fileExtensionList) {
          let extensionFound = fileExtensionList.find(
            (validFileExtension) =>
              validFileExtension === file.extension?.toLowerCase()
          );
          setIsValidFileExtension(extensionFound ? false : true);
        }

        setIsFileEmpty(false);
        const nameWithoutExt = getFileNameWithoutExtension(raw.name);
        const blobUrl = URL.createObjectURL(raw);
        uploadedFiles.push({
          file: raw,
          name: nameWithoutExt,
          blobUrl: blobUrl,
          category: bulkCategory || ''
        });

        if (!isMultipleFiles) {
          setResourceName(nameWithoutExt);
        }
      }
    });

    setFiles(uploadedFiles);
  };

  const handleSetAllCategoryChange = (e: ComboBoxChangeEvent) => {
    const selectedCategory = e.value || '';
    if (!selectedCategory) {
      setBulkCategory('');
      return;
    }

    const hasExistingCategories = files.some(
      (f) => f.category && f.category.trim() !== ''
    );
    if (hasExistingCategories) {
      setPendingBulkCategory(selectedCategory);
      setShowOverwriteConfirm(true);
    } else {
      const updatedFiles = files.map((f) => ({
        ...f,
        category: selectedCategory
      }));
      setFiles(updatedFiles);
      setBulkCategory(selectedCategory);
    }
  };

  const handleConfirmOverwrite = (result: YesNoDialogResult) => {
    if (result === 'yes' && pendingBulkCategory) {
      const updatedFiles = files.map((f) => ({
        ...f,
        category: pendingBulkCategory
      }));
      setFiles(updatedFiles);
      setBulkCategory(pendingBulkCategory);
    }
    setPendingBulkCategory('');
    setShowOverwriteConfirm(false);
  };

  const loadFile = () => {
    if (files.length > 0) {
      const blobUrl = URL.createObjectURL(files[files.length - 1].file);
      const fileName = files[files.length - 1].file.name;
      const mimeType = files[files.length - 1].file.type;
      const size = files[files.length - 1].file.size;

      setLastImageUrl(blobUrl);
      setIsImageValid(true);
      setLastUploadedImage({
        ...lastUploadedImage,
        blobUrl,
        mimeType,
        fileName,
        size,
        name: isMultipleFiles
          ? files[files.length - 1].name ||
            getFileNameWithoutExtension(fileName)
          : resourceName,
        category: resourceCategory
      });
    }
  };

  const onMediaChange = (url: string) => {
    if (url === '') {
      setLastUploadedImage({} as UploadedImage);
      setIsFileEmpty(true);
      setIsImageValid(false);
      setIsError('');
    }

    const videoPlatform = detectVideoPlatform(url);

    if (!videoPlatform.id && url) {
      const blobUrl = url;

      fetch(url, {
        method: 'HEAD',
        mode: 'no-cors'
      })
        .then((res) => {
          const fileName = url.split('/').pop()!;
          const nameWithoutExt = getFileNameWithoutExtension(fileName);
          setResourceName(nameWithoutExt);
          const fileSize = res.headers.get('Content-Length');
          let mimeType = res.headers.get('Content-Type')!;
          const maxImageSizeInByte =
            customFileSize !== undefined
              ? customFileSize
              : sizeLimit[mimeType as keyof typeof sizeLimit];
          checkFileSize(maxImageSizeInByte ?? 0, parseInt(fileSize ?? ''));

          // Handle special case for image URLs that not return Content-Type
          if (!mimeType) {
            mimeType = 'image';
          }
          setLastUploadedImage({
            ...lastUploadedImage,
            blobUrl,
            mimeType,
            fileName,
            name: nameWithoutExt,
            category: resourceCategory
          });
          setIsFileEmpty(false);

          // remove upload and resource if exists
          setFiles([]);
          setLastImageId('');
          setIsImageValid(true);
        })
        .catch((err) => {
          setLastImageUrl('');
          setIsError('Image has been blocked by CORS policy');
          setIsImageValid(false);
        });
    }

    if (videoPlatform.id) {
      setLastUploadedImage({
        mimeType: videoPlatform.platform ?? '',
        fileName: `${videoPlatform.platform}-resource`,
        blobUrl: videoPlatform.id ?? '',
        size: 0
      });
      setIsFileEmpty(false);
    }
    setLastImageUrl(url);
  };

  const convertFilesToMedia = (): UploadedImage[] => {
    let allFiles: UploadedImage[] = [];
    files.forEach((file) => {
      const blobUrl = file.blobUrl || URL.createObjectURL(file.file);
      const fileName = file.file.name;
      const mimeType = file.file.type;
      const size = file.file.size;

      allFiles.push({
        blobUrl: blobUrl,
        mimeType: mimeType,
        fileName: fileName,
        size: size,
        name: isSupportUpload
          ? file.file.name
          : file.name || getFileNameWithoutExtension(fileName),
        category: isSupportUpload ? '' : file.category || ''
      });
    });
    return allFiles;
  };

  const handleClose = (file: BulkMediaItem) => {
    if (files) {
      const fls = [...files];

      const index = fls.indexOf(file);
      if (index > -1) {
        // only splice array when item is found
        fls.splice(index, 1); // 2nd parameter means remove one item only
      }
      setFiles(fls);
      setLastUploadedImage({} as UploadedImage);
      setIsLargeImageSize(false);
      setIsValidFileExtension(false);
      setIsFileEmpty(true);

      // remove URL and resource if exists
      setLastImageUrl('');
      setLastImageId('');
    }
  };

  const onSelectedImageResource = (e: RadioButtonChangeEvent) => {
    if (e.value && state.gameDocument) {
      const resource = GetResourceById(state?.gameDocument, e.value);
      setLastImageId(e.value);
      if (resource) {
        setLastImageUrl(resource?.value ? resource?.value : '');
        const blobUrl = resource?.value ? resource?.value : '';
        const mimeType = resource?.type ? resource.type : '';
        const fileName = resource?.name ? resource.name : '';
        const fileSize = resource?.size ? resource.size : 0;

        if (!fileSize) {
          fetch(blobUrl, {
            method: 'HEAD',
            mode: 'no-cors'
          }).then((res) => {
            const fileSize = res.headers.get('Content-Length');
            let maxImageSizeInByte =
              customFileSize !== undefined
                ? customFileSize
                : sizeLimit[mimeType as keyof typeof sizeLimit];

            if (imageUsage !== undefined)
              maxImageSizeInByte = sizeLimit.worldZoneImage - imageUsage;

            if (
              mapImages !== undefined &&
              mapImages.find((m) => m.imageResId === resource.id)
            )
              maxImageSizeInByte = Infinity;

            checkFileSize(maxImageSizeInByte ?? 0, parseInt(fileSize ?? ''));
          });
        } else {
          let maxImageSizeInByte =
            customFileSize !== undefined
              ? customFileSize
              : sizeLimit[mimeType as keyof typeof sizeLimit];

          if (imageUsage !== undefined)
            maxImageSizeInByte = sizeLimit.worldZoneImage - imageUsage;

          if (
            mapImages !== undefined &&
            mapImages.find((m) => m.imageResId === resource.id)
          )
            maxImageSizeInByte = Infinity;

          checkFileSize(maxImageSizeInByte ?? 0, fileSize);
        }

        setLastUploadedImage({
          ...lastUploadedImage,
          blobUrl,
          mimeType,
          fileName,
          resource: resource,
          size: fileSize
        });
        setIsImageValid(true);
        setIsFileEmpty(false);
      }
    }
  };

  const onChangeSearchResource = (e: InputChangeEvent) => {
    setSearchResourceText((e.target.value as string) ?? '');
  };

  const acceptableMessage = useMemo(() => {
    if (imageUsage !== undefined) {
      const usedMb = formatBytes(imageUsage, 2);

      if (imageUsage >= sizeLimit.worldZoneImage) {
        return `Image formats only. ${usedMb} used. Max 3MB reached.`;
      }
      return `Image formats only. Map Images for This Map ${usedMb} used of 3 MB. ${formatBytes(sizeLimit.worldZoneImage - imageUsage, 2)} remaining.`;
    }

    const allowAll =
      fileType.length === 0 ||
      (fileType.includes('image') &&
        fileType.includes('audio') &&
        fileType.includes('video'));

    if (allowAll) {
      const parts = [
        `Image (${formatBytes(sizeLimit.image, 0)})`,
        `audio (${formatBytes(sizeLimit.audio, 0)})`,
        `or video (${formatBytes(sizeLimit.video, 0)})`
      ];
      return `${parts.join(', ')} formats only.`;
    }

    if (fileType.length === 1) {
      const type = fileType[0];
      const size =
        customFileSize !== undefined ? customFileSize : sizeLimit[type];
      const sizeText = size > 0 ? `Max ${formatBytes(size, 0)}.` : '';

      return `${capitalize(type)} formats only.${size > 0 ? ' ' + sizeText : ''}`;
    }

    const parts = fileType.map((type) => {
      const size =
        customFileSize !== undefined ? customFileSize : sizeLimit[type];
      const sizeText = size > 0 ? ` (${formatBytes(size, 0)})` : '';
      return `${capitalize(type)}${sizeText}`;
    });

    let finalList = '';
    if (parts.length === 1) {
      finalList = parts[0];
    } else {
      finalList = parts.slice(0, -1).join(', ') + ' or ' + parts.slice(-1);
    }

    return `${finalList} formats only.`;
  }, [resourceCategory, imageUsage, fileType]);

  useEffect(
    () => {
      loadFile();

      if (files.length > 0) {
        setShowDefaultPhoto(false);
      } else {
        setShowDefaultPhoto(true);
      }
    },
    // eslint-disable-next-line
    [files]
  );

  useEffect(() => {
    const effectiveUrl =
      imageUrl ||
      (resourceId && state?.gameDocument
        ? GetResourceById(state.gameDocument, resourceId)?.value ?? ''
        : '');

    if ((effectiveUrl || resourceId) && state?.gameDocument) {
      const imageResource = resourceId
        ? GetResourceById(state.gameDocument, resourceId)
        : GetResourceByValue(state.gameDocument, effectiveUrl);
      setLastImageId(imageResource?.id!);

      const finalUrl = effectiveUrl || imageResource?.value || '';
      setLastImageUrl(finalUrl);
      setShowDefaultPhoto(false);
      setLastUploadedImage({
        ...lastUploadedImage,
        blobUrl: finalUrl,
        name: imageResource?.name ?? '',
        category: imageResource?.category ?? '',
        resource: imageResource,
        mimeType: imageResource?.type ? `${imageResource.type}/` : ''
      });
      setIsImageValid(true);
      setIsFileEmpty(false);
    } else {
      setLastImageUrl('');
    }
  }, []);

  useEffect(() => {
    if (!isMultipleFiles) {
      setLastUploadedImage((prev) => ({
        ...prev,
        name: resourceName,
        category: resourceCategory
      }));
    }
  }, [resourceName, resourceCategory, isMultipleFiles]);


  const onRemoveCategory = (categoryName: string) => {
    const categories = cloneDeep(
      state.gameDocument?.assets.additionalResourceCategories ?? []
    );
    const index = categories.indexOf(categoryName);
    if (index > -1) {
      categories.splice(index, 1);
    }

    const resourcesList = cloneDeep(state.gameDocument?.resources ?? []).map(
      (resource) => {
        if (resource.category === categoryName) {
          resource.category = '';
        }
        return resource;
      }
    );

    if (resourceCategory === categoryName) {
      setResourceCategory('');
    }

    setState((prev) =>
      UpdateGameDocState(prev, {
        ...prev.gameDocument!,
        resources: resourcesList,
        assets: {
          ...prev.gameDocument?.assets,
          additionalResourceCategories: categories
        }
      })
    );
  };


  const [categoryFilter, setCategoryFilter] = useState<string>('');

  const filteredCategories = useMemo(() => {
    if (!categoryFilter) return allCategories;
    return allCategories.filter((c) =>
      c.toLowerCase().includes(categoryFilter.toLowerCase())
    );
  }, [allCategories, categoryFilter]);

  const handleCategoryChange = (e: ComboBoxChangeEvent) => {
    setResourceCategory(e.value || '');
  };

  const handleCategoryFilterChange = (e: any) => {
    setCategoryFilter(e.filter?.value || '');
  };

  const isNameValid = !isNameInvalid(resourceName);

  // Shared Title + Category fields component
  const renderTitleCategoryFields = () => {
    if (isSupportUpload) return null;

    if (isMultipleFiles) {
      return (
        <div className="mb-4 p-3 rounded-3 bg-light border">
          <label className="form-label fw-bold text-dark mb-1 d-flex align-items-center gap-2">
            <span className="material-symbols-outlined text-primary">
              group_work
            </span>
            Set Category for All Files
          </label>
          <small className="text-muted d-block mb-2">
            Apply a selected category to all files in the current upload list.
            This will overwrite individual categories.
          </small>
          <ResourceCategoryComboBox
            category={filteredCategories}
            value={bulkCategory || null}
            onChange={handleSetAllCategoryChange}
            onRemoveCategory={onRemoveCategory}
            className="w-100"
            clearButton={true}
          />
        </div>
      );
    }

    return (
      <div className="mb-3">
        {/* Title field */}
        <div className="mb-3">
          <label className="form-label fw-bold d-block mb-1">
            Title <span style={{ color: '#dc3545' }}>*</span>
          </label>
          <small className="text-muted d-block mb-1">
            Give your media a clear, user-friendly name.
          </small>
          <div className="position-relative">
            <input
              type="text"
              className="k-textbox w-100"
              value={resourceName}
              onChange={(e) => setResourceName(e.target.value)}
              placeholder="Enter a title for this media"
              style={{ paddingRight: '36px' }}
            />
            {isNameValid && resourceName.trim() !== '' && (
              <span
                className="material-symbols-outlined position-absolute"
                style={{
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#28a745',
                  fontSize: '20px'
                }}>
                check_circle
              </span>
            )}
          </div>
          {isNameInvalid(resourceName) && resourceName !== '' ? (
            <Error>Name is required and cannot be a UUID.</Error>
          ) : (
            <small className="text-muted d-block text-end">
              This title will help you identify the media later.
            </small>
          )}
        </div>

        {/* Category field */}
        <div className="mb-3">
          <label className="form-label fw-bold d-block mb-1">
            Category <span style={{ color: '#dc3545' }}>*</span>
          </label>
          <small className="text-muted d-block mb-1">
            Choose an existing category or create a new one.
          </small>
          <ResourceCategoryComboBox
            category={filteredCategories}
            value={resourceCategory || null}
            onChange={handleCategoryChange}
            onRemoveCategory={onRemoveCategory}
            className="w-100"
            clearButton={true}
          />
        </div>
      </div>
    );
  };

  const renderPreviewMedia = () => {
    if (!existingResource || !existingResource?.type) return;

    const mediaType = existingResource.type;

    return (
      <div className={'mb-3'}>
        <label className="form-label fw-bold d-block mb-1">Preview</label>
        <div className={'d-flex align-items-center justify-content-center'}>
          {mediaType === 'video' ? (
            <video
              key={existingResource.value}
              className="video"
              controls
              style={{
                maxHeight: '200px'
              }}>
              <source src={existingResource.value} type="video/mp4"></source>
            </video>
          ) : mediaType === 'image' ? (
            <img
              style={{
                objectFit: 'cover',
                width: 'auto',
                maxHeight: '200px'
              }}
              src={existingResource.value}
              alt=""
            />
          ) : mediaType === 'audio' ? (
            <audio src={existingResource.value} controls autoPlay>
              <source src={existingResource.value} type="audio/mpeg" />
            </audio>
          ) : mediaType == 'pdf' ? (
            <PdfViewerComponent
              source={existingResource.value ?? ''}></PdfViewerComponent>
          ) : (
            ['youtube', 'vimeo'].includes(mediaType) && (
              <iframe
                src={
                  mediaType === 'youtube'
                    ? `https://www.youtube.com/embed/${existingResource.value}`
                    : `https://player.vimeo.com/video/${existingResource.value}`
                }
                width={400}
                height={200}
                referrerPolicy={'strict-origin-when-cross-origin'}
                allow={
                  'autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share'
                }
                allowFullScreen
              />
            )
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <Window
        modal={true}
        className={'shadow'}
        initialWidth={740}
        initialHeight={640}
        minimizeButton={() => null}
        maximizeButton={() => null}
        onClose={() => toggleDialog()}
        title={title}
        {...props}>
        <TabStrip selected={selected} onSelect={onTabSelect} animation={false}>
          <TabStripTab title="Upload" disabled={hideUploadTab}>
            <>
              {renderPreviewMedia()}
              {renderTitleCategoryFields()}

              {showDefaultPhoto &&
                fileType.length === 0 &&
                fileType.includes('image') && (
                  <div
                    className={
                      'round-card-upload rounded-full h-7 w-7 mx-auto'
                    }>
                    <div className={'mask'}>
                      <Button
                        className={'border border-1 text-white p-0'}
                        fillMode={'flat'}
                        rounded={'full'}
                        size={'small'}
                      />
                    </div>
                  </div>
                )}

              {!isMultipleFiles &&
                !showDefaultPhoto &&
                fileType.length === 0 &&
                fileType.includes('image') && (
                  <div className={'text-center mb-2'}>
                    <img
                      src={lastImageUrl}
                      alt={''}
                      className={'img-fluid rounded'}
                    />
                  </div>
                )}

              <ExternalDropZone
                className={`mb-2 text-center${lastImageUrl ? '' : ' h-100'}`}
                uploadRef={uploadRef}
                customNote={acceptableMessage}
                customHint={
                  <>
                    Drop files to upload
                    <br />
                    or{' '}
                    <span
                      className={'text-primary cursor-pointer'}
                      onClick={onUploadClick}>
                      browse
                    </span>
                  </>
                }
              />

              <Upload
                className={'d-none'}
                ref={uploadRef}
                batch={false}
                multiple={isMultipleFiles}
                withCredentials={false}
                restrictions={{
                  allowedExtensions: [allowedExtension]
                }}
                accept={allowedExtension}
                onAdd={onDropFiles}
                showFileList={false}
                saveUrl={(files) => {
                  return new Promise((resolve) => {
                    resolve({ uid: files[0].uid });
                  });
                }}
              />

              {isValidFileExtension ? (
                <Error>Invalid File Type: {acceptableMessage}</Error>
              ) : (
                isLargeImageSize && (
                  <Error>
                    File exceeds the {maxImageSize} limit. Please upload a
                    smaller file.
                  </Error>
                )
              )}

              {isMultipleFiles ? (
                <div className="d-flex flex-column gap-3 w-100 mt-2">
                  {files &&
                    files.map((file, index) => {
                      const isImage = file.file.type.startsWith('image/');
                      const isVideo = file.file.type.startsWith('video/');
                      const isPdf = file.file.type.includes('pdf');

                      return (
                        <div
                          className="card border p-3 rounded-3 shadow-sm d-flex flex-row align-items-stretch gap-3"
                          key={index}
                          style={{ backgroundColor: '#ffffff' }}>
                          {/* A. Thumbnail preview if available */}
                          <div
                            className="d-flex align-items-center justify-content-center border rounded-3 bg-light overflow-hidden flex-shrink-0"
                            style={{
                              width: '96px',
                              height: '96px',
                              minWidth: '96px'
                            }}>
                            {isImage && file.blobUrl ? (
                              <img
                                src={file.blobUrl}
                                alt=""
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                              />
                            ) : isVideo && file.blobUrl ? (
                              <div className="position-relative w-100 h-100 d-flex align-items-center justify-content-center bg-dark">
                                <video
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                  }}>
                                  <source
                                    src={file.blobUrl}
                                    type={file.file.type}
                                  />
                                </video>
                                <span
                                  className="material-symbols-outlined text-white position-absolute"
                                  style={{ fontSize: '24px', opacity: 0.8 }}>
                                  play_circle
                                </span>
                              </div>
                            ) : isPdf ? (
                              <span
                                className="material-symbols-outlined text-danger"
                                style={{ fontSize: '48px' }}>
                                picture_as_pdf
                              </span>
                            ) : (
                              <span
                                className="material-symbols-outlined text-secondary"
                                style={{ fontSize: '48px' }}>
                                insert_drive_file
                              </span>
                            )}
                          </div>

                          {/* B. Editable columns depending on support upload */}
                          <div className="d-flex flex-grow-1 flex-column gap-3 justify-content-center">
                            {isSupportUpload ? (
                              // Support page upload (Simple info, no editing or validation)
                              <div className="d-flex flex-column justify-content-center">
                                <span
                                  className="fw-semibold text-dark text-break"
                                  style={{ fontSize: '14px' }}>
                                  {file.file.name}
                                </span>
                                <small className="text-muted">
                                  {formatBytes(file.file.size)}
                                </small>
                              </div>
                            ) : (
                              // Standard multiple files upload (Editable name & category - Stacked Vertically)
                              <>
                                {/* Name column */}
                                <div className="w-100">
                                  <label
                                    className="form-label fw-semibold text-muted mb-1"
                                    style={{ fontSize: '12px' }}>
                                    Name{' '}
                                    <span style={{ color: '#dc3545' }}>*</span>
                                  </label>
                                  <input
                                    type="text"
                                    className="k-textbox w-100"
                                    value={file.name || ''}
                                    onChange={(e) => {
                                      const updatedFiles = [...files];
                                      updatedFiles[index].name = e.target.value;
                                      setFiles(updatedFiles);
                                    }}
                                    placeholder="File Name"
                                  />
                                  {isNameInvalid(file.name || '') ? (
                                    <Error
                                      style={{
                                        fontSize: '11px',
                                        marginTop: '4px'
                                      }}>
                                      Name is required and cannot be a UUID.
                                    </Error>
                                  ) : (
                                    <small
                                      className="text-muted d-block text-break mt-1"
                                      style={{ fontSize: '11px' }}>
                                      Source: {file.file.name} (
                                      {formatBytes(file.file.size)})
                                    </small>
                                  )}
                                </div>

                                {/* Category column */}
                                <div className="w-100">
                                  <label
                                    className="form-label fw-semibold text-muted mb-1"
                                    style={{ fontSize: '12px' }}>
                                    Category{' '}
                                    <span style={{ color: '#dc3545' }}>*</span>
                                  </label>
                                  <ResourceCategoryComboBox
                                    category={filteredCategories}
                                    value={file.category || null}
                                    onChange={(e) => {
                                      const updatedFiles = [...files];
                                      updatedFiles[index].category =
                                        e.value || '';
                                      setFiles(updatedFiles);
                                    }}
                                    onRemoveCategory={onRemoveCategory}
                                    className="w-100"
                                    clearButton={true}
                                  />
                                  {!file.category ||
                                  file.category.trim() === '' ? (
                                    <Error
                                      style={{
                                        fontSize: '11px',
                                        marginTop: '4px'
                                      }}>
                                      Category is required.
                                    </Error>
                                  ) : null}
                                </div>
                              </>
                            )}
                          </div>

                          {/* Delete Button */}
                          <div className="d-flex align-items-center flex-shrink-0">
                            <Button
                              themeColor="error"
                              fillMode="flat"
                              style={{
                                height: '40px',
                                width: '40px',
                                padding: 0
                              }}
                              onClick={() => handleClose(file)}>
                              <span className="material-symbols-outlined">
                                delete
                              </span>
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <ul className={'list-group w-100 mt-2'}>
                  {files &&
                    files.map((file, index) => (
                      <li
                        className={
                          'list-group-item rounded-0 d-flex justify-content-between align-items-center p-2'
                        }
                        key={index}>
                        <span className={'material-symbols-outlined ms-2 me-2'}>
                          add_photo_alternate
                        </span>
                        <span className={'text-break w-100'}>
                          {file.file.name} {`(${formatBytes(file.file.size)})`}
                        </span>
                        <Button
                          themeColor={'error'}
                          fillMode={'flat'}
                          onClick={() => handleClose(file)}>
                          <span className={'material-symbols-outlined'}>
                            delete
                          </span>
                        </Button>
                      </li>
                    ))}
                </ul>
              )}
            </>
          </TabStripTab>
          <TabStripTab title="Link" disabled={hideUrlTab}>
            <>
              {renderPreviewMedia()}

              {/* Title field */}
              {!isSupportUpload && !isMultipleFiles && (
                <div className="mb-3">
                  <label className="form-label fw-bold d-block mb-1">
                    Title <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <small className="text-muted d-block mb-1">
                    Give your media a clear, user-friendly name.
                  </small>
                  <div className="position-relative">
                    <input
                      type="text"
                      className="k-textbox w-100"
                      value={resourceName}
                      onChange={(e) => setResourceName(e.target.value)}
                      placeholder="Enter a title for this media"
                      style={{ paddingRight: '36px' }}
                    />
                    {isNameValid && resourceName.trim() !== '' && (
                      <span
                        className="material-symbols-outlined position-absolute"
                        style={{
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#28a745',
                          fontSize: '20px'
                        }}>
                        check_circle
                      </span>
                    )}
                  </div>
                  {isNameInvalid(resourceName) && resourceName !== '' ? (
                    <Error>Name is required and cannot be a UUID.</Error>
                  ) : (
                    <small className="text-muted d-block text-end">
                      This title will help you identify the media later.
                    </small>
                  )}
                </div>
              )}

              {/* URL field */}
              <div className="mb-3">
                <label className="form-label fw-bold d-block mb-1">
                  URL <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <small className="text-muted d-block mb-1">
                  Paste a link to the media.
                </small>
                <div className="position-relative">
                  <input
                    type="text"
                    className="k-textbox w-100"
                    id="k-editor-image-url"
                    value={lastImageUrl}
                    disabled={/^data:image/.test(lastImageUrl || '')}
                    placeholder="https://example.com/media/asset.png"
                    onChange={(e) => onMediaChange(e.target.value)}
                  />
                </div>
                {isError !== '' ? (
                  <Error>{isError}</Error>
                ) : (
                  <small className="text-muted d-block">
                    Supports links to publicly accessible media.
                  </small>
                )}
              </div>

              {/* Category field */}
              {!isSupportUpload && (
                <div className="mb-3">
                  <label className="form-label fw-bold d-block mb-1">
                    Category <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <small className="text-muted d-block mb-1">
                    Choose an existing category or create a new one.
                  </small>
                  <ResourceCategoryComboBox
                    category={filteredCategories}
                    value={resourceCategory || null}
                    onChange={handleCategoryChange}
                    onRemoveCategory={onRemoveCategory}
                    className="w-100"
                    clearButton={true}
                  />
                </div>
              )}

              {/* Visual Box "Link added" (Full-Width) or Video iframe */}
              {detectVideoPlatform(lastImageUrl).platform ? (
                <div className="mt-4 text-center">
                  {detectVideoPlatform(lastImageUrl).platform === 'youtube' && (
                    <iframe
                      src={`https://www.youtube.com/embed/${detectVideoPlatform(lastImageUrl).id}`}
                      width="100%"
                      height={250}
                      className="rounded border"
                      referrerPolicy={'strict-origin-when-cross-origin'}
                      allow={
                        'autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share'
                      }
                      allowFullScreen
                    />
                  )}

                  {detectVideoPlatform(lastImageUrl).platform === 'vimeo' && (
                    <iframe
                      src={`https://player.vimeo.com/video/${detectVideoPlatform(lastImageUrl).id}`}
                      width="100%"
                      height={250}
                      className="rounded border"
                      referrerPolicy={'strict-origin-when-cross-origin'}
                      allow={
                        'autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share'
                      }
                      allowFullScreen
                    />
                  )}
                </div>
              ) : (
                <div className="mt-4">
                  <div
                    className="border rounded d-flex flex-column align-items-center justify-content-center p-4 text-center w-100"
                    style={{
                      borderStyle: 'dashed',
                      borderWidth: '2px',
                      borderColor: '#dee2e6',
                      backgroundColor: '#f8f9fa',
                      minHeight: '160px'
                    }}>
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center mb-3"
                      style={{
                        width: '56px',
                        height: '56px',
                        backgroundColor: '#e9ecef',
                        transform: 'rotate(-45deg)'
                      }}>
                      <span
                        className="material-symbols-outlined text-secondary"
                        style={{
                          fontSize: '28px',
                          transform: 'rotate(45deg)'
                        }}>
                        link
                      </span>
                    </div>
                    <h6 className="fw-bold mb-1" style={{ color: '#1d252d' }}>
                      Link added
                    </h6>
                    <p className="text-muted mb-0" style={{ fontSize: '12px' }}>
                      This media will be linked to the provided URL.
                    </p>
                  </div>
                </div>
              )}

              {isLargeImageSize && (
                <Error className="mt-2">
                  File exceeds the {maxImageSize} limit. Please upload a smaller
                  file.
                </Error>
              )}
            </>
          </TabStripTab>
          <TabStripTab title="Media Library" disabled={hideResourcesTab}>
            <>
              {renderPreviewMedia()}
              <div className="d-flex align-items-center gap-2 mb-2">
                <DropDownList
                  style={{ width: '220px' }}
                  data={mediaLibraryCategoryOptions}
                  value={selectedMediaCategory}
                  onChange={(e: DropDownListChangeEvent) =>
                    setSelectedMediaCategory(e.value)
                  }
                />
                <Input
                  className="flex-grow-1"
                  type="text"
                  placeholder="search"
                  value={searchResourceText}
                  onChange={onChangeSearchResource}
                />
                <Checkbox
                  label={'Show system defaults'}
                  checked={showSystemDefaults}
                  onChange={(e) => setShowSystemDefaults(Boolean(e.value))}
                />
              </div>
              <div className={'d-flex flex-column'}>
                {filteredMediaResources.length === 0 ? (
                  <div className="p-4 text-center text-muted">
                    No resources found.
                  </div>
                ) : (
                  filteredMediaResources.map((item) => (
                    <div
                      key={item.id}
                      className={'d-flex flex-column gap-1 p-2 border-bottom'}>
                      <div className={'d-flex align-items-center'}>
                        <div className={'d-flex h-100'}>
                          <RadioButton
                            name={'resource-list'}
                            className={'d-flex align-items-center'}
                            id={item.id}
                            value={item.id}
                            onChange={(e) => onSelectedImageResource(e)}
                            checked={item.id === lastImageId}
                          />
                        </div>
                        <Label
                          editorId={item.id}
                          className="d-flex align-items-center cursor-pointer text-decoration-none fw-normal">
                          <div className={'cell-thumbnail-container mx-2'}>
                            {item.type === 'image' ? (
                              <img
                                className={'w-100 h-100'}
                                src={item.value}
                                alt=""
                              />
                            ) : item.type === 'pdf' ? (
                              <PdfViewerComponent source={item.value ?? ''} />
                            ) : (
                              <video className={'rounded w-100 h-100'}>
                                <source src={item.value} type={'video/mp4'} />
                              </video>
                            )}
                          </div>
                          <span>{item.name}</span>
                        </Label>
                      </div>
                      {item.id === lastImageId && isLargeImageSize && (
                        <Error>
                          File exceeds the {maxImageSize} limit. Please upload a
                          smaller file.
                        </Error>
                      )}
                    </div>
                  ))
                )}
              </div>
            </>
          </TabStripTab>
        </TabStrip>

        <WindowActionsBar>
          <Button themeColor={'secondary'} onClick={() => toggleDialog()}>
            Cancel
          </Button>
          <Button
            themeColor={'primary'}
            onClick={() => {
              if (isMultipleFiles) {
                onSubmitMultipleFiles(convertFilesToMedia());
              } else {
                onSubmit(lastUploadedImage);
              }
            }}
            disabled={isSaveDisabled}>
            Save
          </Button>
        </WindowActionsBar>
      </Window>

      {showOverwriteConfirm && (
        <YesNoDialog
          title="Overwriting Categories"
          onConfirm={handleConfirmOverwrite}
          onClose={() => setShowOverwriteConfirm(false)}>
          Some files already have categories. Are you sure you want to overwrite
          all categories with "{pendingBulkCategory}"?
        </YesNoDialog>
      )}
    </>
  );
};
